export type GroupedTenantConfigurations = Record<string, TenantConfiguration[]>

export function groupTenantConfigurations(
  tenantConfig: TenantConfiguration[]
): GroupedTenantConfigurations {
  const sortedConfig = [...tenantConfig].sort((a, b) => a.group.localeCompare(b.group))
  return sortedConfig.reduce((acc, config) => {
    const { group } = config
    acc[group] = (acc[group] || []).concat(config)
    return acc
  }, {} as GroupedTenantConfigurations)
}

export function transformGroupNameToTitleCase(groupName: string) {
  return groupName
    .split(' ')
    .map((word) => word.charAt(0).toLocaleUpperCase() + word.slice(1))
    .join(' ')
}

export function mergeConfigurationWithValue(
  configurations: TenantConfigResponse[],
  data?: KeyValuePair[]
) {
  return configurations.map((config) => {
    const valueObj = data?.find((dataItem) => dataItem.key === config.key)
    const isJson = config.dataType.toLowerCase() === 'json'
    const value = isJson ? JSON.parse(valueObj?.value ?? 'null') : valueObj?.value
    return { ...config, value }
  })
}

export function normalizeTenantConfigurationValue(value: any, dataType: string) {
  if (typeof value === 'object') return JSON.stringify(value)

  const normalizedValue = String(value ?? '').trim()
  return ['int', 'bigint', 'bigdecimal'].includes(dataType?.toLowerCase()) && normalizedValue === ''
    ? 'null'
    : normalizedValue
}

export function buildGroupedTenantConfigurations(
  configurables: TenantConfigResponse[],
  data: KeyValuePair[] = []
): GroupedTenantConfigurations {
  return groupTenantConfigurations(mergeConfigurationWithValue(configurables, data))
}

export function flattenGroupedTenantConfigurations(
  fields: GroupedTenantConfigurations
): KeyValuePair[] {
  return Object.values(fields)
    .flat()
    .map(({ key, value, dataType }) => ({
      key,
      value: normalizeTenantConfigurationValue(value, dataType),
    }))
}
